/* eslint-disable @typescript-eslint/no-dynamic-delete */
/* eslint-disable no-param-reassign */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  ClearFieldErrorPayload,
  FieldErrorsState,
  FieldMetadataErrors,
  SetFieldErrorPayload,
  ValidateFieldMetadataPayload,
} from '../types/builderTypes';

import { FIELD_PROPERTIES } from '../../../../shared/constants';
import {
  validateElementId,
  validateErrorMessage,
  validateExpression,
  validateExpressionValue,
  validateFieldLabel,
  validateFixedGroupOptionLabel,
  validateFixedGroupOptionValue,
  validateHelptext,
  validatePlaceholder,
  validateRegexPattern,
  validateRichText,
  validateTnCLink,
  validateTnCMessage,
  validateTooltip,
  validateUnifiedLimit,
} from '../../data-layer/validation/fieldMetadataValidators';

export interface ReconcileOptionLabelErrorsPayload {
  fieldId: string;
  allowedValues: string[];
}

const initialState: FieldErrorsState = {};

const fieldErrorsSlice = createSlice({
  name: 'builder/fieldErrors',
  initialState,
  reducers: {
    setFieldError: (state, action: PayloadAction<SetFieldErrorPayload>) => {
      const { fieldId, fieldName, error } = action.payload;

      if (!state[fieldId]) {
        state[fieldId] = {};
      }

      state[fieldId][fieldName as keyof FieldMetadataErrors] = error;
    },

    clearFieldError: (state, action: PayloadAction<ClearFieldErrorPayload>) => {
      const { fieldId, fieldName } = action.payload;

      if (state[fieldId]) {
        const field = fieldName as keyof FieldMetadataErrors;

        // Delete the specific error field
        delete state[fieldId][field];

        // If no more errors for this field, remove the field entirely
        if (Object.keys(state[fieldId]).length === 0) {
          delete state[fieldId];
        }
      }
    },

    clearAllFieldErrors: (state, action: PayloadAction<string>) => {
      const fieldId = action.payload;

      delete state[fieldId];
    },

    reconcileFixedGroupOptionLabelErrorsForField: (
      state,
      action: PayloadAction<ReconcileOptionLabelErrorsPayload>
    ) => {
      const { fieldId, allowedValues } = action.payload;
      const bucket = state[fieldId];

      if (!bucket) {
        return;
      }

      const prefix = `${FIELD_PROPERTIES.FIXED_GROUP_OPTION_LABEL}.`;

      Object.keys(bucket).forEach((key) => {
        if (key.startsWith(prefix)) {
          const suffix = key.slice(prefix.length);

          if (!allowedValues.includes(suffix)) {
            delete bucket[key as keyof FieldMetadataErrors];
          }
        }
      });

      if (Object.keys(bucket).length === 0) {
        delete state[fieldId];
      }
    },

    validateFieldMetadata: (
      state,
      action: PayloadAction<ValidateFieldMetadataPayload>
    ) => {
      const {
        fieldId,
        fieldName,
        value,
        existingElementIds = [],
        tncType,
        fieldType,
      } = action.payload;
      let error: string | null = null;

      switch (fieldName) {
        case FIELD_PROPERTIES.LABEL: {
          error = validateFieldLabel(value, fieldType);
          break;
        }

        case FIELD_PROPERTIES.ELEMENT_ID: {
          error = validateElementId(value, existingElementIds);
          break;
        }

        case FIELD_PROPERTIES.PLACEHOLDER: {
          error = validatePlaceholder(value);
          break;
        }

        case FIELD_PROPERTIES.HELPER_TEXT: {
          error = validateHelptext(value);
          break;
        }

        // All fields required messages
        case FIELD_PROPERTIES.REQUIRED_ERROR_MESSAGE: {
          error = validateErrorMessage(value, false);
          break;
        }

        case FIELD_PROPERTIES.MIN_MAX_ERROR_MESSAGE: {
          error = validateErrorMessage(value, false);
          break;
        }

        case FIELD_PROPERTIES.REGEX_ERROR_MESSAGE: {
          error = validateErrorMessage(value, true);
          break;
        }

        case FIELD_PROPERTIES.REGEX_PATTERN: {
          error = validateRegexPattern(value);
          break;
        }

        case FIELD_PROPERTIES.TOOLTIP: {
          error = validateTooltip(value);
          break;
        }

        case FIELD_PROPERTIES.DEFAULT_VALUE_EXPRESSION: {
          error = validateExpression(value);
          break;
        }

        case FIELD_PROPERTIES.SHOW_WHEN_EXPRESSION: {
          error = validateExpressionValue(value);
          break;
        }

        case FIELD_PROPERTIES.HIDE_WHEN_EXPRESSION: {
          error = validateExpressionValue(value);
          break;
        }

        case FIELD_PROPERTIES.REQUIRED_WHEN_EXPRESSION: {
          error = validateExpressionValue(value);
          break;
        }

        case FIELD_PROPERTIES.NOT_REQUIRED_WHEN_EXPRESSION: {
          error = validateExpressionValue(value);
          break;
        }

        case FIELD_PROPERTIES.READ_ONLY_EXPRESSION: {
          error = validateExpressionValue(value);
          break;
        }

        case FIELD_PROPERTIES.EDITABLE_EXPRESSION: {
          error = validateExpressionValue(value);
          break;
        }

        case FIELD_PROPERTIES.EXPRESSION_VALIDATION: {
          error = validateExpressionValue(value);
          break;
        }

        case FIELD_PROPERTIES.EXPRESSION_ERROR_MESSAGE: {
          error = validateErrorMessage(value, true);
          break;
        }

        case FIELD_PROPERTIES.TNC_LINK: {
          error = validateTnCLink(value, tncType || '');
          break;
        }

        case FIELD_PROPERTIES.TNC_MESSAGE: {
          error = validateTnCMessage(value);
          break;
        }

        case FIELD_PROPERTIES.RICHTEXT: {
          error = validateRichText(value);
          break;
        }

        case FIELD_PROPERTIES.UNIFIED_LIMIT: {
          try {
            const { minValue, maxValue, isNumeric } = JSON.parse(value) as {
              minValue?: string | number;
              maxValue?: string | number;
              isNumeric?: boolean;
            };

            error = validateUnifiedLimit(minValue, maxValue, isNumeric);
          } catch {
            error = null;
          }

          break;
        }

        case FIELD_PROPERTIES.FIXED_GROUP_OPTION_LABEL: {
          error = validateFixedGroupOptionLabel(value);
          break;
        }

        case FIELD_PROPERTIES.FIXED_GROUP_OPTION_VALUE: {
          error = validateFixedGroupOptionValue(value);
          break;
        }

        default: {
          // Handle fixed group option labels with dynamic suffixes
          if (
            fieldName.startsWith(
              `${FIELD_PROPERTIES.FIXED_GROUP_OPTION_LABEL}.`
            )
          ) {
            error = validateFixedGroupOptionLabel(value);
            break;
          }

          // Handle fixed group option values with dynamic suffixes
          if (
            fieldName.startsWith(
              `${FIELD_PROPERTIES.FIXED_GROUP_OPTION_VALUE}.`
            )
          ) {
            error = validateFixedGroupOptionValue(value);
            break;
          }

          return;
        }
      }

      if (error) {
        // Set error
        if (!state[fieldId]) {
          state[fieldId] = {};
        }

        state[fieldId][fieldName as keyof FieldMetadataErrors] = error;
      } else if (state[fieldId]) {
        // Clear error
        delete state[fieldId][fieldName as keyof FieldMetadataErrors];

        // If no more errors for this field, remove the field entirely
        if (Object.keys(state[fieldId]).length === 0) {
          delete state[fieldId];
        }
      }
    },
  },
});

export const { reducer: fieldErrorsReducer, actions: fieldErrorsActions } =
  fieldErrorsSlice;
